import type { ReactNode } from "react";
import { useMemo } from "react";
import { Box, Grid } from "@mui/material";
import {
  getCampaignStatusText,
  campaignFormatHelper,
  getStatusCode,
} from "@/helpers/utils";
import LearnMoreButton from "@/components/muiCustom/buttons/LearnMoreButton";
import StatusChip from "@/components/muiCustom/chip/StatusChip";
import DigitalCampaignJoinButton from "./DigitalCampaignJoinButton";
import { CAMPAIGN_CONST } from "@/config/constants";
import TagChipContainer from "@/components/muiCustom/chip/TagChip";
import JoinByDate from "@/features/campaigns/components/joinCampaigns/JoinByDate";
import {
  cardDetailSx,
  cardWrapperSx,
  brandMatchTagSx,
} from "@/features/campaigns/shared/styles/campaignCardSx";
import type { DigitalCampaignCardData } from "./digitalCampaignTypes";

const DRAFT_BORDER_STYLE = {
  border: "1px solid var(--neutral-200)",
  borderRadius: "var(--radius-container)",
};

type CampaignDetailProps = {
  label: string;
  value?: ReactNode;
  valueClassName?: string;
};

type DigitalCampaignCardProps = {
  campaign?: DigitalCampaignCardData;
  isJoinWindowPending?: string | false;
  participantId?: string | number;
};

type RouterTo = {
  pathname: string;
  state: {
    campaignId?: string | number;
    from: string;
  };
};

const CampaignDetail = ({
  label,
  value,
  valueClassName = "",
}: CampaignDetailProps) => (
  <div className="detail">
    <div className="title">{label}</div>
    <div className={`title-val ${valueClassName}`}>{value}</div>
  </div>
);

type DateDetailProps = {
  label: string;
  value?: ReactNode;
};

const DateDetail = ({ label, value }: DateDetailProps) => (
  <div className="detail">
    <div className="title">{label}</div>
    <div className="title-val" style={{ fontSize: "0.625rem" }}>
      {value}
    </div>
  </div>
);

const DigitalCampaignCard = ({
  campaign,
  isJoinWindowPending,
  participantId,
}: DigitalCampaignCardProps) => {
  const {
    status,
    is_enrolled,
    brand_contribution,
    brand_match_message,
    tags,
    is_draft,
    availability_window,
    evergreen_participant_date_flag,
  } = campaign || {};

  const formatter = useMemo(() => campaignFormatHelper(campaign), [campaign]);

  const hideDate = useMemo(() => {
    if (!availability_window && evergreen_participant_date_flag) return true;
    return (
      availability_window ===
      CAMPAIGN_CONST.AVAILABILITY_WINDOW.TYPE.ALWAYS_AVAILABLE
    );
  }, [availability_window, evergreen_participant_date_flag]);

  const isActiveOrCompleted =
    status === CAMPAIGN_CONST.STATUS.ACTIVE.ID ||
    status === CAMPAIGN_CONST.STATUS.COMPLETED.ID;

  const isActiveOrScheduled =
    status === CAMPAIGN_CONST.STATUS.ACTIVE.ID ||
    status === CAMPAIGN_CONST.STATUS.SCHEDULED.ID;

  const showEnrolledStatus =
    !!is_enrolled &&
    !availability_window &&
    !evergreen_participant_date_flag &&
    isActiveOrScheduled;

  return (
    <Box
      className="h-100 position-relative fade-in"
      style={is_draft ? DRAFT_BORDER_STYLE : {}}
      sx={{ ...cardDetailSx, ...cardWrapperSx, ...brandMatchTagSx }}
    >
      <div
        className={`card h-100 ${!!brand_contribution ? "brand-match" : ""}`}
      >
        <div className="card-body pr-3 pl-3 pt-4 pb-0 position-relative border-bottom">
          {!!brand_contribution && brand_match_message && (
            <div className="brand-match-tag" title="Eligible for Brand Match">
              <span className="tag">{brand_match_message}</span>
            </div>
          )}

          <Grid
            container
            alignItems="center"
            spacing={1}
            className={brand_match_message ? "pt-3" : ""}
          >
            <Grid size="grow">
              <CampaignDetail
                label="Campaign Name"
                value={formatter.name()}
                valueClassName={`campaign-name ${isActiveOrCompleted ? "main" : ""}`}
              />
            </Grid>
            {is_draft && (
              <Grid size="auto">
                <StatusChip
                  label={getCampaignStatusText(5)}
                  status={getStatusCode("draft")}
                />
              </Grid>
            )}
            {showEnrolledStatus && (
              <Grid size="auto">
                <StatusChip
                  label={getCampaignStatusText(status)}
                  status={getStatusCode(status)}
                />
              </Grid>
            )}
          </Grid>

          {Array.isArray(tags) && tags.length > 0 && (
            <Box mt={1}>
              <TagChipContainer tags={tags} />
            </Box>
          )}

          <Grid container spacing={2} my={2}>
            <Grid size={6}>
              <CampaignDetail
                label="Channel"
                value={formatter.getChannelNames()}
                valueClassName="pr-2"
              />
            </Grid>
            {!hideDate && (
              <Grid size={6}>
                <Grid container direction="column" spacing={1}>
                  <Grid>
                    <DateDetail
                      label={
                        isJoinWindowPending
                          ? "Available Starting"
                          : "Start Date"
                      }
                      value={formatter.startDate("MMM DD, YYYY")}
                    />
                  </Grid>
                  <Grid>
                    <DateDetail
                      label={isJoinWindowPending ? "Join By" : "End Date"}
                      value={formatter.endDate("MMM DD, YYYY")}
                    />
                  </Grid>
                </Grid>
              </Grid>
            )}
          </Grid>
        </div>

        <div className="card-footer pt-3" style={{ minHeight: "72px" }}>
          <Grid container alignItems="center" spacing={1}>
            <Grid size={4}>
              <JoinByDate joinByDate={isJoinWindowPending as string} />
            </Grid>
            <Grid size={2} sx={{ textAlign: "right" }}>
              <LearnMoreButton
                to={
                  {
                    pathname: `/dashboard/learn-more/digital-campaign/${campaign?.id}`,
                    state: { campaignId: campaign?.id, from: "/dashboard" },
                  } as RouterTo
                }
                icon
                imagePath="/img/icons/bulb_pink.svg"
              />
            </Grid>
            <Grid size={6}>
              <DigitalCampaignJoinButton
                campaign={campaign}
                participantId={participantId}
              />
            </Grid>
          </Grid>
        </div>
      </div>
    </Box>
  );
};

export default DigitalCampaignCard;
